import React, {useEffect, useMemo, useRef, useState} from 'react';

const WINDOW = 2;

const STEPS = [
  {
    key: 'learning',
    label: 'Learn',
    icon: '📖',
    number: 1,
    borderColor: 'var(--lcv-blue)',
    background: 'rgba(66, 165, 245, 0.12)',
    /** Viewer phase to land on when this step is clicked. */
    targetPhase: 'learning',
    /** Minimum termRank required for this step to be considered "done". */
    doneRank: 1,
  },
  {
    key: 'workbook',
    label: 'Work',
    icon: '✏️',
    number: 2,
    borderColor: 'var(--lcv-orange)',
    background: 'rgba(255, 167, 38, 0.12)',
    targetPhase: 'workbook_ready',
    doneRank: 2,
  },
  {
    key: 'knowledge_check',
    label: 'Check',
    icon: '🎯',
    number: 3,
    borderColor: 'var(--lcv-green)',
    background: 'rgba(102, 187, 106, 0.12)',
    targetPhase: 'knowledge_check',
    doneRank: 3,
  },
];

function getEncouragement(stats, termsDone, totalTerms) {
  if (!stats || stats.answersTotal === 0) {
    return termsDone === 0
      ? "Let's get started!"
      : `${termsDone} of ${totalTerms} terms done`;
  }

  const accuracy = Math.round((stats.answersCorrect / stats.answersTotal) * 100);
  const lastFive = stats.answerTimestamps.slice(-5);
  const isRushing =
    lastFive.length >= 2 &&
    (lastFive[lastFive.length - 1] - lastFive[0]) / (lastFive.length - 1) < 3000 &&
    accuracy < 50;

  if (isRushing) return 'Slow down and read carefully.';
  if (termsDone >= totalTerms && accuracy >= 80) {
    return `All ${totalTerms} terms done at ${accuracy}%`;
  }
  if (accuracy >= 80) return `${accuracy}% accuracy - you're crushing it`;
  if (accuracy >= 60) return `${accuracy}% accuracy - solid work`;
  return `${accuracy}% accuracy - keep trying`;
}

const LCVProgressTracker = ({data, phase, mode = 'full', onNavigate}) => {
  if (!data) return null;

  const {
    terms,
    activeTermIndex,
    activeSubPhase,
    introComplete,
    masteryReached,
    termsDone,
    termRanks,
    stats,
  } = data;

  const totalSlots = terms.length;
  const [windowStart, setWindowStart] = useState(
    Math.max(0, activeTermIndex - (WINDOW - 1)),
  );
  const [hovered, setHovered] = useState(false);
  const [celebratingIds, setCelebratingIds] = useState([]);

  const getWindowStartForActiveState = () => {
    if (masteryReached) {
      return Math.max(0, totalSlots - WINDOW);
    }

    if (activeTermIndex < 0) return 0;
    return Math.max(0, Math.min(activeTermIndex - (WINDOW - 1), totalSlots - WINDOW));
  };

  const currentStepIndex = STEPS.findIndex(step => step.key === activeSubPhase);
  const isIntroActive = phase === 'frontloading';
  const isMasteryActive = phase === 'mastery' || phase === 'results';
  const isMasteryDone = phase === 'results';
  const isKnowledgeCheckSummary = phase === 'knowledge_check_summary';
  const isExpanded = mode === 'full' || hovered;

  useEffect(() => {
    if (masteryReached) {
      setWindowStart(Math.max(0, totalSlots - WINDOW));
      return;
    }

    setWindowStart(prev => {
      if (activeTermIndex < prev) return activeTermIndex;
      if (activeTermIndex >= prev + WINDOW) return activeTermIndex - (WINDOW - 1);
      return prev;
    });
  }, [activeTermIndex, masteryReached, totalSlots]);

  useEffect(() => {
    if (!isExpanded) {
      setWindowStart(getWindowStartForActiveState());
    }
  }, [isExpanded, activeTermIndex, masteryReached, totalSlots]);

  const canScrollPrev = windowStart > 0;
  const canScrollNext = windowStart + WINDOW < totalSlots;
  const visibleTermIndices = Array.from({length: WINDOW}, (_, i) => windowStart + i)
    .filter(i => i < totalSlots);
  const encouragement = getEncouragement(stats, termsDone, totalSlots);
  const safeTermRanks = Array.isArray(termRanks) ? termRanks : [];
  const getTermRank = termIndex => safeTermRanks[termIndex] ?? 0;
  const getTermDone = termIndex => getTermRank(termIndex) >= 3;
  /**
   * Mastery is gated until every key term in the lesson has its
   * knowledge check checked off (termRank >= 3 ≡ persisted status
   * `completed`). Falls back to the phase-based `masteryReached` once
   * the learner has actually entered the mastery flow so the node still
   * unlocks during in-session play.
   */
  const allKnowledgeChecksDone =
    totalSlots > 0 && safeTermRanks.length === totalSlots
      ? safeTermRanks.every(rank => rank >= 3)
      : false;
  const masteryUnlocked = masteryReached || allKnowledgeChecksDone;

  const doneStates = useMemo(
    () => ({
      intro: introComplete && !isIntroActive,
      mastery: isMasteryDone,
      ...terms.reduce((acc, _, termIndex) => {
        acc[`term-${termIndex}`] = getTermDone(termIndex);
        return acc;
      }, {}),
    }),
    [terms, introComplete, isIntroActive, isMasteryDone, safeTermRanks],
  );
  const previousDoneStatesRef = useRef(doneStates);

  useEffect(() => {
    const previousDoneStates = previousDoneStatesRef.current;
    const newlyCompleted = Object.keys(doneStates).filter(
      key => doneStates[key] && !previousDoneStates[key],
    );

    previousDoneStatesRef.current = doneStates;

    if (!newlyCompleted.length) return undefined;

    setCelebratingIds(current => Array.from(new Set([...current, ...newlyCompleted])));
    const clearTimer = window.setTimeout(() => {
      setCelebratingIds(current =>
        current.filter(id => !newlyCompleted.includes(id)),
      );
    }, 2000);

    return () => window.clearTimeout(clearTimer);
  }, [doneStates]);

  const canNavigate = typeof onNavigate === 'function';

  /**
   * Decide whether a term/step is reachable by click. Past sections (any
   * step the learner has already cleared, persisted or session) are
   * always reachable. The currently-active step is reachable only when
   * we're already on it (so clicking it is a no-op rather than a regress).
   */
  const handleStepClick = (termIndex, step) => {
    if (!canNavigate) return;
    onNavigate({termIndex, phase: step.targetPhase});
  };

  return (
    <div
      className={[
        'lcv-progress-bar',
        `lcv-progress-bar-window-${WINDOW}`,
        isExpanded ? 'lcv-progress-bar-expanded' : 'lcv-progress-bar-mini',
      ].join(' ')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="lcv-pt-header">
        <span className="lcv-pt-title">Progress Tracker</span>
        <span className="lcv-pt-encourage">{encouragement}</span>
      </div>

      <div className="lcv-pt-body">
        <button
          type="button"
          className={[
            'lcv-pt-node',
            'lcv-pt-node-intro',
            isIntroActive ? 'lcv-pt-node-active' : '',
            introComplete && !isIntroActive ? 'lcv-pt-node-done' : '',
            celebratingIds.includes('intro') ? 'lcv-pt-node-complete-pop' : '',
          ].filter(Boolean).join(' ')}
          disabled
        >
          <div className="lcv-pt-node-header">
            <span className="lcv-pt-num">{introComplete && !isIntroActive ? '✓' : '📋'}</span>
            <span className="lcv-pt-name">Intro</span>
          </div>
        </button>

        <div className="lcv-pt-term-rail">
          <button
            type="button"
            className={[
              'lcv-pt-cycle-btn',
              !isExpanded ? 'lcv-pt-cycle-spacer' : '',
              canScrollPrev ? '' : 'lcv-pt-cycle-hidden',
            ].filter(Boolean).join(' ')}
            onClick={() => setWindowStart(start => Math.max(0, start - 1))}
            disabled={!canScrollPrev || !isExpanded}
            aria-label="Show previous terms"
          >
            ‹
          </button>

          <div className="lcv-pt-terms">
            {visibleTermIndices.map(termIndex => {
              const term = terms[termIndex];
              const termRank = getTermRank(termIndex);
              const termDone = getTermDone(termIndex);
              const termActive =
                !isIntroActive &&
                !isMasteryActive &&
                !isKnowledgeCheckSummary &&
                termIndex === activeTermIndex;
              const termLocked = !masteryReached && termIndex > activeTermIndex && termRank === 0;

              return (
                <div
                  key={termIndex}
                  className={[
                    'lcv-pt-node',
                    'lcv-pt-node-term',
                    termActive ? 'lcv-pt-node-active' : '',
                    termDone ? 'lcv-pt-node-done' : '',
                    termLocked ? 'lcv-pt-node-locked' : '',
                    celebratingIds.includes(`term-${termIndex}`)
                      ? 'lcv-pt-node-complete-pop'
                      : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="lcv-pt-node-header">
                    <span className="lcv-pt-num">{termDone ? '✓' : termIndex + 1}</span>
                    <span className="lcv-pt-name">{term.term}</span>
                  </div>

                  <div className="lcv-pt-steps">
                    {STEPS.map((step, stepIndex) => {
                      const isDone = termRank >= step.doneRank;
                      const isActive = termActive && stepIndex === currentStepIndex;
                      // LEARN is always reachable — it's the entry point to a
                      // term and re-reading the definition shouldn't be gated.
                      // WORK / CHECK still require the learner to have either
                      // cleared them previously or to currently be on them.
                      const isAlwaysReachable = step.key === 'learning';
                      const isClickable =
                        canNavigate &&
                        (isAlwaysReachable || isDone || isActive);
                      // Don't grey out steps the learner can actually click;
                      // greying a clickable button reads as "disabled".
                      const isLocked = !isDone && !isActive && !isClickable;

                      const className = [
                        'lcv-pt-step',
                        isActive ? 'lcv-pt-step-active' : '',
                        isDone ? 'lcv-pt-step-done' : '',
                        isLocked ? 'lcv-pt-step-locked' : '',
                        isClickable ? 'lcv-pt-step-clickable' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');

                      return (
                        <button
                          key={step.key}
                          type="button"
                          className={className}
                          style={
                            isActive
                              ? {
                                  borderColor: step.borderColor,
                                  background: step.background,
                                }
                              : undefined
                          }
                          disabled={!isClickable}
                          onClick={
                            isClickable
                              ? () => handleStepClick(termIndex, step)
                              : undefined
                          }
                          aria-label={`Go to ${term.term} ${step.label}`}
                          title={
                            isClickable
                              ? `Go back to ${term.term} — ${step.label}`
                              : undefined
                          }
                        >
                          <span className="lcv-pt-step-icon">{isDone ? '✓' : step.icon}</span>
                          <span className="lcv-pt-step-label">
                            {step.number}. {step.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className={[
              'lcv-pt-cycle-btn',
              !isExpanded ? 'lcv-pt-cycle-spacer' : '',
              canScrollNext ? '' : 'lcv-pt-cycle-hidden',
            ].filter(Boolean).join(' ')}
            onClick={() =>
              setWindowStart(start => Math.min(Math.max(0, totalSlots - WINDOW), start + 1))
            }
            disabled={!canScrollNext || !isExpanded}
            aria-label="Show next terms"
          >
            ›
          </button>
        </div>

        <button
          type="button"
          className={[
            'lcv-pt-node',
            'lcv-pt-node-mastery',
            isMasteryActive ? 'lcv-pt-node-active' : '',
            isMasteryDone ? 'lcv-pt-node-done' : '',
            !masteryUnlocked ? 'lcv-pt-node-locked' : '',
            masteryUnlocked && canNavigate ? 'lcv-pt-node-clickable' : '',
            celebratingIds.includes('mastery') ? 'lcv-pt-node-complete-pop' : '',
          ].filter(Boolean).join(' ')}
          disabled={!masteryUnlocked || !canNavigate}
          onClick={
            masteryUnlocked && canNavigate
              ? () =>
                  onNavigate({
                    termIndex: Math.max(0, totalSlots - 1),
                    phase: 'mastery',
                  })
              : undefined
          }
          aria-label={
            masteryUnlocked
              ? 'Go to Mastery test'
              : 'Mastery — locked until all knowledge checks are complete'
          }
          title={
            masteryUnlocked
              ? undefined
              : 'Finish every term\u2019s knowledge check to unlock the mastery test.'
          }
        >
          <div className="lcv-pt-node-header">
            <span className="lcv-pt-num">{isMasteryDone ? '✓' : '🏆'}</span>
            <span className="lcv-pt-name">Mastery</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default LCVProgressTracker;
